import { useMemo, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { AdminTable, type ColDef } from "@/components/admin/shared/AdminTable";
import { AdminModal } from "@/components/admin/shared/AdminModal";
import { DeleteConfirmModal } from "@/components/admin/shared/DeleteConfirmModal";
import {
  PageHeader,
  AddButton,
  SaveButton,
  CancelButton,
  ViewButton,
  EditButton,
  DeleteButton,
} from "@/components/admin/shared/PageHeader";
import { TablePagination } from "@/components/admin/shared/TablePagination";
import { TableSearch } from "@/components/admin/shared/TableSearch";
import { PennField, PennInput, PennDropdown } from "@/components/ui";
import { useDebouncedSearch } from "@/hooks/useDebouncedSearch";
import {
  useAutorivFasteners,
  useAutorivFastener,
  useAutorivFastenerGroups,
  useCreateAutorivFastener,
  useDeleteAutorivFastener,
  useUpdateAutorivFastener,
} from "@/hooks/useAutorivFasteners";
import type { AutorivFastener } from "@/api/autorivFasteners";

const apiErrorMessage = (err: unknown, fallback: string): string => {
  if (err && typeof err === "object" && "response" in err) {
    const data = (err as { response?: { data?: { error?: { message?: string } } } }).response?.data;
    if (data?.error?.message) return data.error.message;
  }
  return err instanceof Error ? err.message : fallback;
};

const autorivFormSchema = z.object({
  fastenerType: z.string().trim().min(1, "Type is required").max(30),
  studNut: z
    .union([z.literal(""), z.literal("Stud"), z.literal("Nut")])
    .optional(),
  fastenerGroup: z.string().trim().max(20).optional(),
  installationForceTons: z
    .union([z.literal(""), z.coerce.number().nonnegative("Must be non-negative")])
    .optional(),
  installationForceKn: z
    .union([z.literal(""), z.coerce.number().nonnegative("Must be non-negative")])
    .optional(),
});
type AutorivFormData = z.infer<typeof autorivFormSchema>;

const blank: AutorivFormData = {
  fastenerType: "",
  studNut: "",
  fastenerGroup: "",
  installationForceTons: "",
  installationForceKn: "",
};

const StudNutBadge = ({ value }: { value: string | null }) => {
  if (!value) return <>—</>;
  return (
    <span className="inline-flex items-center rounded-full bg-gray-100 dark:bg-gray-800 px-3 py-0.5 text-blue_grey text-small-medium">
      {value}
    </span>
  );
};

const GroupBadge = ({ value }: { value: string | null }) => {
  if (!value) return <>—</>;
  return (
    <span className="inline-flex items-center rounded-full bg-green-50 dark:bg-green-900/30 px-3 py-0.5 text-green-700 dark:text-green-300 text-small-medium">
      {value}
    </span>
  );
};

export const AutorivFastenersPage = ({
  title = "Autoriv Fasteners",
  subtitle = "Dataset: Autoriv Fasteners",
}: {
  title?: string;
  subtitle?: string;
}) => {
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const { searchInput, setSearchInput, search, clearSearch } = useDebouncedSearch(() => setPageIndex(0));

  const params = useMemo(
    () => ({
      page: pageIndex + 1,
      limit: pageSize,
      search: search || undefined,
    }),
    [pageIndex, pageSize, search],
  );

  const { data, isLoading, isFetching, isError, error } =
    useAutorivFasteners(params);
  const rows: AutorivFastener[] = data?.data ?? [];
  const pagination = data?.pagination;
  const total = pagination?.total ?? 0;
  const hasNext = pagination ? pagination.page < pagination.totalPages : false;

  const groupsQuery = useAutorivFastenerGroups();
  const knownGroups = groupsQuery.data?.groups ?? [];

  type ModalMode = "add" | "edit" | "view";
  type ModalState = { mode: ModalMode; item?: AutorivFastener } | null;

  const [modal, setModal] = useState<ModalState>(null);
  const [deleteTarget, setDeleteTarget] = useState<AutorivFastener | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const editingType = modal?.mode !== "add" ? modal?.item?.fastenerType ?? null : null;
  const detail = useAutorivFastener(editingType);
  const createMutation = useCreateAutorivFastener();
  const updateMutation = useUpdateAutorivFastener();
  const deleteMutation = useDeleteAutorivFastener();

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<AutorivFormData>({ resolver: zodResolver(autorivFormSchema) });

  const fillForm = (item: AutorivFastener) => {
    reset({
      fastenerType: item.fastenerType,
      studNut: (item.studNut as "Stud" | "Nut" | null) ?? "",
      fastenerGroup: item.fastenerGroup ?? "",
      installationForceTons:
        item.installationForceTons === null ? "" : Number(item.installationForceTons),
      installationForceKn:
        item.installationForceKn === null ? "" : Number(item.installationForceKn),
    });
  };

  const openAdd = () => {
    setFormError(null);
    reset(blank);
    setModal({ mode: "add" });
  };
  const openEdit = (item: AutorivFastener) => {
    setFormError(null);
    fillForm(item);
    setModal({ mode: "edit", item });
  };
  const openView = (item: AutorivFastener) => {
    setFormError(null);
    fillForm(item);
    setModal({ mode: "view", item });
  };
  const closeModal = () => {
    setModal(null);
    setFormError(null);
    reset(blank);
  };

  const onSubmit = (form: AutorivFormData) => {
    if (!modal || modal.mode === "view") return;
    setFormError(null);
    const studNut = form.studNut === "" || form.studNut === undefined ? null : form.studNut;
    const fastenerGroup = form.fastenerGroup?.trim() || null;
    const installationForceTons =
      form.installationForceTons === "" || form.installationForceTons === undefined
        ? null
        : String(form.installationForceTons);
    const installationForceKn =
      form.installationForceKn === "" || form.installationForceKn === undefined
        ? null
        : String(form.installationForceKn);

    if (modal.mode === "edit" && modal.item) {
      updateMutation.mutate(
        {
          fastenerType: modal.item.fastenerType,
          body: { studNut, fastenerGroup, installationForceTons, installationForceKn },
        },
        {
          onSuccess: closeModal,
          onError: (e) =>
            setFormError(apiErrorMessage(e, "Failed to save autoriv fastener.")),
        },
      );
    } else {
      createMutation.mutate(
        {
          fastenerType: form.fastenerType.trim(),
          studNut,
          fastenerGroup,
          installationForceTons,
          installationForceKn,
        },
        {
          onSuccess: closeModal,
          onError: (e) =>
            setFormError(apiErrorMessage(e, "Failed to create autoriv fastener.")),
        },
      );
    }
  };

  const isView = modal?.mode === "view";
  const isAdd = modal?.mode === "add";

  const cols: ColDef<AutorivFastener>[] = [
    { header: "Type", render: (i) => i.fastenerType },
    { header: "Stud/Nut", render: (i) => <StudNutBadge value={i.studNut} /> },
    { header: "Group", render: (i) => <GroupBadge value={i.fastenerGroup} /> },
    { header: "Force (t)", render: (i) => i.installationForceTons ?? "—" },
    { header: "Force (kN)", render: (i) => i.installationForceKn ?? "—" },
    {
      header: "Actions",
      render: (i) => (
        <div className="flex space-x-2">
          <ViewButton onClick={() => openView(i)} />
          <EditButton onClick={() => openEdit(i)} />
          <DeleteButton onClick={() => setDeleteTarget(i)} />
        </div>
      ),
    },
  ];

  const saving = createMutation.isPending || updateMutation.isPending;
  const titleMap: Record<ModalMode, string> = {
    add: "Add Autoriv Fasteners record",
    edit: "Edit Autoriv Fasteners record",
    view: "View Autoriv Fasteners record",
  };

  return (
    <>
      <PageHeader
        title={title}
        subtitle={`${subtitle} · ${total} records`}
        action={<AddButton onClick={openAdd} />}
      />

      {isError && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700 text-body">
          Failed to load autoriv fasteners
          {error instanceof Error ? `: ${error.message}` : "."}
        </div>
      )}

      {deleteError && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700 text-body">
          {deleteError}
        </div>
      )}

      <TableSearch
        value={searchInput}
        onChange={setSearchInput}
        onClear={clearSearch}
        placeholder="Search type, stud/nut, or group…"
      />

      <AdminTable
        columns={cols}
        rows={rows}
        keyExtractor={(i) => i.fastenerType}
        loading={isLoading}
      />

      <TablePagination
        pageIndex={pageIndex}
        pageSize={pageSize}
        rowsOnPage={rows.length}
        hasNext={hasNext}
        busy={isFetching}
        onPrev={() => setPageIndex((p) => Math.max(0, p - 1))}
        onNext={() => setPageIndex((p) => p + 1)}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPageIndex(0);
        }}
      />

      <AdminModal
        open={modal !== null}
        onClose={closeModal}
        title={modal ? titleMap[modal.mode] : ""}
        footer={
          isView ? (
            <CancelButton onClick={closeModal} />
          ) : (
            <>
              <CancelButton onClick={closeModal} />
              <SaveButton
                onClick={handleSubmit(onSubmit)}
                label={saving ? "Saving…" : "Save"}
              />
            </>
          )
        }
      >
        {modal?.mode !== "add" && detail.isLoading ? (
          <div className="py-10 text-center text-blue_grey4 text-body">Loading…</div>
        ) : (
          <form className="grid grid-cols-1 sm:grid-cols-2 gap-4" onSubmit={handleSubmit(onSubmit)}>
            {formError && (
              <div className="col-span-1 sm:col-span-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-red-700 text-body">
                {formError}
              </div>
            )}

            <PennField
              label="Type"
              htmlFor="fastenerType"
              required={isAdd}
              error={isAdd ? errors.fastenerType?.message : undefined}
            >
              <PennInput
                id="fastenerType"
                disabled={!isAdd}
                error={isAdd && !!errors.fastenerType}
                {...register("fastenerType")}
              />
            </PennField>

            <PennField
              label="Stud / Nut"
              htmlFor="studNut"
              error={!isView ? errors.studNut?.message : undefined}
            >
              <Controller
                control={control}
                name="studNut"
                render={({ field }) => (
                  <PennDropdown
                    inputId="studNut"
                    options={[
                      { value: "Stud", label: "Stud" },
                      { value: "Nut", label: "Nut" },
                    ]}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    isDisabled={isView}
                    placeholder="—"
                  />
                )}
              />
            </PennField>

            <PennField
              label="Fastener group"
              htmlFor="fastenerGroup"
              error={!isView ? errors.fastenerGroup?.message : undefined}
            >
              <Controller
                control={control}
                name="fastenerGroup"
                render={({ field }) => (
                  <PennDropdown
                    inputId="fastenerGroup"
                    options={knownGroups.map((g) => ({ value: g, label: g }))}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    isDisabled={isView}
                    placeholder="—"
                  />
                )}
              />
            </PennField>

            <PennField
              label="Installation force (tons)"
              htmlFor="installationForceTons"
              error={!isView ? errors.installationForceTons?.message : undefined}
            >
              <PennInput
                id="installationForceTons"
                type="number"
                step="0.01"
                disabled={isView}
                error={!isView && !!errors.installationForceTons}
                {...register("installationForceTons")}
              />
            </PennField>

            <PennField
              label="Installation force (kN)"
              htmlFor="installationForceKn"
              error={!isView ? errors.installationForceKn?.message : undefined}
            >
              <PennInput
                id="installationForceKn"
                type="number"
                step="0.01"
                disabled={isView}
                error={!isView && !!errors.installationForceKn}
                {...register("installationForceKn")}
              />
            </PennField>
          </form>
        )}
      </AdminModal>

      <DeleteConfirmModal
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => {
          if (!deleteTarget) return;
          setDeleteError(null);
          deleteMutation.mutate(deleteTarget.fastenerType, {
            onError: (e) =>
              setDeleteError(apiErrorMessage(e, "Failed to delete autoriv fastener.")),
            onSettled: () => setDeleteTarget(null),
          });
        }}
        itemLabel={deleteTarget?.fastenerType}
      />
    </>
  );
};
